import Qs from "qs";
import { IExploreFilters, IOnChangeAction } from "./explore-filters.types";
import {
    IInteractionsGetQueryParameters,
    InteractionMetric,
    ITag,
    ITagGroup,
    TagGroupType,
} from "@edgetier/watchtower-types";
import { trimOrNull } from "@edgetier/utilities";

/**
 * Turn the form's filters into an object for requesting data from the backend and updating the URL.
 * @param values            Form values.
 * @param onChangeCallback  Change callback applied to parameters
 * @returns                 URL query parameters and object of filters.
 */
export const formatSearch = (
    values: IExploreFilters,
    tagGroupsDetails: ITagGroup[],
    onChangeCallback?: IOnChangeAction
) => {
    const {
        tagGroups: formTagGroups,
        isAgentSearch,
        isBotSearch,
        isCustomerSearch,
        isSystemSearch,
        isMessageSearch,
        isMetadataSearch,
        ...nonTagParameters
    } = values;

    const formTagGroupsKeys = Object.keys(formTagGroups);

    // Split tag groups form filter values into tag group type
    const { agentTagGroups, tagGroups } = tagGroupsDetails.reduce(
        (result, tagGroup) => {
            if (!formTagGroupsKeys.includes(tagGroup.tagGroupId)) {
                return {
                    agentTagGroups: { ...result.agentTagGroups },
                    tagGroups: { ...result.tagGroups },
                };
            }
            return {
                agentTagGroups: {
                    ...result.agentTagGroups,
                    ...(tagGroup.tagGroupType === TagGroupType.Agent
                        ? { [tagGroup.tagGroupId]: formTagGroups[tagGroup.tagGroupId] }
                        : {}),
                },
                tagGroups: {
                    ...result.tagGroups,
                    ...(tagGroup.tagGroupType !== TagGroupType.Agent
                        ? { [tagGroup.tagGroupId]: formTagGroups[tagGroup.tagGroupId] }
                        : {}),
                },
            };
        },
        { agentTagGroups: {}, tagGroups: {} }
    );
    const searchText = trimOrNull(values.search);

    // Additional check for message parameters
    const messageSearchParameters = {
        isAgentSearch: typeof searchText === "string" ? isAgentSearch : undefined,
        isBotSearch: typeof searchText === "string" ? isBotSearch : undefined,
        isCustomerSearch: typeof searchText === "string" ? isCustomerSearch : undefined,
        isSystemSearch: typeof searchText === "string" ? isSystemSearch : undefined,
        isMessageSearch: typeof searchText === "string" ? isMessageSearch : undefined,
        isMetadataSearch: typeof searchText === "string" ? isMetadataSearch : undefined,
    };

    const parameters = {
        ...Qs.parse(window.location.search, { ignoreQueryPrefix: true }),
        ...nonTagParameters,
        ...messageSearchParameters,
        search: searchText,
        tagId: Object.values(tagGroupsDetails.length > 0 ? tagGroups : formTagGroups).flatMap((tagId) => tagId),
        agentTagId: Object.values(agentTagGroups).flatMap((tagId) => tagId),
    };

    const searchValues = typeof onChangeCallback === "function" ? onChangeCallback(parameters) : parameters;

    return {
        search: Qs.stringify(searchValues, { addQueryPrefix: true, arrayFormat: "repeat", skipNulls: true }),
        searchValues,
    };
};

/**
 * Find the forms initial values.
 * @param searchParameters URL search parameters.
 * @param groupedTags      Tags grouped by tag group ID.
 * @returns
 */
export const getInitialValues = (
    { tagId, agentTagId, ...otherFilters }: IInteractionsGetQueryParameters,
    groupedTags: Record<string, ITag[]>
): IExploreFilters => {
    // Tags selected according to the URL.
    const selectedTags = tagId ?? [];

    // Agent tags selected according to the URL.
    const selectedAgentTags = agentTagId ?? [];

    // A different select input will display for each tag group. This object contains the selected tags for each group.
    const tagGroups = Object.entries(groupedTags).map(([tagGroupId, tags]) => [
        tagGroupId,
        tags
            .map(({ tagId }) => tagId)
            .filter((tagId) => selectedTags.includes(tagId) || selectedAgentTags.includes(tagId)),
    ]);

    return {
        tagGroups: Object.fromEntries(tagGroups),
        // Default search type if nothing else is elected
        ...otherFilters,
    };
};

/**
 * Find any metric distribution fields available in query parameters
 * @param searchParameters URL search parameters.
 */
export const getVisibleMetricDistributionFields = (searchParameters: IInteractionsGetQueryParameters): string[] => {
    // Searching for fields starting with "maximum", because this was the name put in dynamic fields
    const visibleFields = Object.keys(searchParameters)
        .filter((filterField) => filterField.startsWith("maximum"))
        .filter((filterField) => {
            // Any parameter starting with `maximum` and it's not a metric will be ignored
            const metricName = filterField
                .replace("maximum", "")
                .replace(/^./, (firstLetter) => firstLetter.toLowerCase());

            return Object.values(InteractionMetric).some((metric) => metric === metricName);
        });
    return visibleFields;
};
